// GetControls.js
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  reset,
  validateEventsAsync,
  setIncludeNonValidated,
  setIncludeOutboundBeacons,
  outputIncludeNonValidated,
  outputIncludeOutboundBeacons,
} from '../../redux/validation/validationResultsSlice';
import { useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import {
  fetchFlowsAsync,
  selectAllFlows,
  selectFlowsLoading,
} from '../../redux/flows/getFlowsSlice';
import { filterFlowsByMarket } from '../../services/flowApiService';
import {
  getDevicesAsync,
  selectDevices,
  selectDevicesByMarket,
} from '../../redux/devices/getDevicesSlice';
import { selectUserEmail } from '../../redux/app/configSlice';
import utc from 'dayjs/plugin/utc';

export default function GetControls() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const userEmail = useSelector(selectUserEmail);
  const includeNonValidated = useSelector(outputIncludeNonValidated);
  const includeOutboundBeacons = useSelector(outputIncludeOutboundBeacons);
  dayjs.extend(utc);

  const [flow, setFlow] = useState('');
  const [testCaseId, setTestCaseId] = useState('');
  const [device, setDevice] = useState('');
  const [deviceIdType, setDeviceIdType] = useState('IDFA');
  const [chooseTimeStartValue, setChooseTimeStartValue] = useState(dayjs());
  const [chooseTimeEndValue, setChooseTimeEndValue] = useState(dayjs());
  const [startTimeLocal, setStartTimeLocal] = useState(
    localStorage.getItem('startTimeLocal') || '',
  );
  const [endTimeLocal, setEndTimeLocal] = useState(localStorage.getItem('endTimeLocal') || '');
  const [error, setError] = useState('');

  // Get selected market from localStorage
  const [selectedMarket, setSelectedMarket] = useState(null);

  // Get backend flows + loading state
  const dbFlows = useSelector(selectAllFlows);
  const isLoadingFlows = useSelector(selectFlowsLoading);

  // Load market and listen for market changes
  useEffect(() => {
    const loadMarket = () => {
      const storedMarket = JSON.parse(localStorage.getItem('market')) || null;
      setSelectedMarket(storedMarket);

      // Reset flow selection when market changes
      if (!storedMarket || storedMarket.id !== selectedMarket?.id) {
        setFlow('');
      }
    };

    loadMarket();

    // Listen for storage changes (market changes from header)
    const handleStorageChange = (e) => {
      if (e.key === 'market') {
        loadMarket();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also listen for custom event for same-window changes
    const handleMarketChange = () => {
      loadMarket();
    };

    window.addEventListener('marketChanged', handleMarketChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('marketChanged', handleMarketChange);
    };
  }, []);

  useEffect(() => {
    if (device !== '' && selectedMarket !== null) {
      setDevice('');
    }
  }, [selectedMarket]);

  // Fetch backend flows when component mounts
  useEffect(() => {
    dispatch(fetchFlowsAsync());
    dispatch(getDevicesAsync());
  }, [dispatch]);

  // Filter flows by market and active status
  const filteredFlows = selectedMarket?.id
    ? filterFlowsByMarket(dbFlows, selectedMarket.id).filter((f) => f.is_active)
    : [];

  // Filter devices by selected market
  const devices =
    useSelector((state) =>
      selectedMarket?.id ? selectDevicesByMarket(state, selectedMarket.id) : [],
    ) || [];

  // Get selected device info for iOS ID type selection
  const selectedDevice = devices.find((d) => d.name === device);
  const isSelectedDeviceIOS = selectedDevice?.type === 'iOS';

  useEffect(() => {
    const searchedRun = location.state?.searchedRun;
    if (searchedRun?.startedAt) {
      const startedAt = dayjs.utc(searchedRun.startedAt).local();
      if (startedAt.isValid()) {
        setChooseTimeStartValue(startedAt);
      }
    }

    if (searchedRun?.finishedAt) {
      // Add one minute to finishedAt before setting end time to ensure the full run duration is captured
      const finishedAt = dayjs.utc(searchedRun.finishedAt).add(1, 'minute').local();
      if (finishedAt.isValid()) {
        setChooseTimeEndValue(finishedAt);
      }
    }
    if (!searchedRun?.deviceId && devices.length === 0) return;
    const matchedDevice = devices.find((d) =>
      [d.device_id_1, d.device_id_2, d.device_id_3]
        .filter(Boolean)
        .some((id) => String(id) === searchedRun?.deviceId),
    );

    if (!matchedDevice) return;
    setDevice(matchedDevice?.name || '');
    //clear the state of navigation
    navigate(location.pathname, {
      replace: true,
      state: null,
    });
  }, [devices, location.state]);

  const handleIncludeOutboundBeacons = (e) => {
    dispatch(setIncludeOutboundBeacons(e.target.checked));
  };

  const handleIncludeNonValidated = (e) => {
    dispatch(setIncludeNonValidated(e.target.checked));
  };

  // Get unique Flow Types from filtered flows
  const flowTypeOptions = Array.from(new Set(filteredFlows.map((f) => f.flow_name))).sort();

  const handleFlowChange = (e) => {
    const name = e.target.value;
    setFlow(name);
    // Reset test case when flow changes
    setTestCaseId('');
  };

  // Get test cases for selected flow type
  const testCases = filteredFlows
    .filter((f) => f.flow_name === flow)
    .map((f) => ({ id: f.test_case_id }));

  const handleDeviceChange = (e) => {
    const deviceName = e.target.value;
    setDevice(deviceName);

    // Reset device ID type when changing devices
    if (deviceName === 'None') {
      setDeviceIdType('IDFA');
    } else {
      const selectedDev = devices.find((d) => d.name === deviceName);
      if (selectedDev?.type === 'iOS') {
        setDeviceIdType('IDFA'); // Default to IDFA for iOS devices
      }
    }
  };

  const dateHandler = (mode) => {
    const ts = Math.floor(Date.now());
    const loc = new Date().toLocaleTimeString();
    if (mode === 'start') {
      setStartTimeLocal(loc);
      setEndTimeLocal('');
      localStorage.setItem('startTimeUnix', String(ts));
      localStorage.setItem('startTimeLocal', loc);
      localStorage.setItem('endTimeUnix', '');
      localStorage.setItem('endTimeLocal', '');
    } else if (mode === 'end') {
      setEndTimeLocal(loc);
      localStorage.setItem('endTimeUnix', String(ts));
      localStorage.setItem('endTimeLocal', loc);
    } else {
      setStartTimeLocal('');
      setEndTimeLocal('');
      localStorage.setItem('startTimeUnix', '');
      localStorage.setItem('startTimeLocal', '');
      localStorage.setItem('endTimeUnix', '');
      localStorage.setItem('endTimeLocal', '');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', m: '0 5px' }}>
      <Box sx={{ minWidth: 120, width: '100%' }}>
        <Stack spacing={2}>
          {/* Flow Type dropdown */}
          <FormControl
            fullWidth
            size="small"
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 1,
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'text.secondary',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'primary.main',
              },
            }}
          >
            <InputLabel id="flow-select-label">Flow Type (Required)</InputLabel>
            <Select
              labelId="flow-select-label"
              value={flow}
              label="Flow Type (Required)"
              onChange={handleFlowChange}
              required
            >
              <MenuItem value="None">Select a Flow Type</MenuItem>
              {flowTypeOptions.map((flowType) => (
                <MenuItem key={flowType} value={flowType}>
                  {flowType}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Test Case dropdown - always visible but disabled when no flow selected */}
          <FormControl
            fullWidth
            size="small"
            disabled={!flow || flow === 'None'}
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 1,
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'text.secondary',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'primary.main',
              },
            }}
          >
            <InputLabel id="test-case-select-label">Test Case (Required)</InputLabel>
            <Select
              labelId="test-case-select-label"
              value={testCaseId}
              label="Test Case (Required)"
              onChange={(e) => setTestCaseId(e.target.value)}
              required
            >
              <MenuItem value="">Select a Test Case</MenuItem>
              {testCases.length > 0 ? (
                testCases.map((tc) => (
                  <MenuItem key={tc.id} value={tc.id}>
                    {tc.id}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  {!flow || flow === 'None'
                    ? 'Select a flow type first'
                    : 'No test cases available'}
                </MenuItem>
              )}
            </Select>
          </FormControl>

          {/* Device dropdown - Using your existing device structure */}
          <FormControl
            fullWidth
            size="small"
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 1,
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'text.secondary',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'primary.main',
              },
            }}
          >
            <InputLabel id="device-select-label">Device (Required)</InputLabel>
            <Select
              labelId="device-select-label"
              value={device}
              label="Device (Required)"
              onChange={handleDeviceChange}
            >
              {devices.length === 0 ? (
                <MenuItem value="None">No Device</MenuItem>
              ) : (
                devices.map((d) => (
                  <MenuItem key={d.id} value={d.name}>
                    {d.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          {/* iOS Device ID Type Selection */}
          {/* {isSelectedDeviceIOS && (
            <FormControl fullWidth size="small" sx={{ bgcolor: "background.paper", borderRadius: 2, boxShadow: 1 }}>
              <InputLabel id="device-id-type-label">Device ID Type</InputLabel>
              <Select
                labelId="device-id-type-label"
                value={deviceIdType}
                label="Device ID Type"
                onChange={(e) => setDeviceIdType(e.target.value)}
              >
                <MenuItem value="IDFA">IDFA</MenuItem>
                <MenuItem value="IDFV">IDFV</MenuItem>
              </Select>
            </FormControl>
          )} */}

          {/* Beacon IDs input - REMOVED since we don't use beacon IDs anymore */}

          {/* Date & Time Pickers - Always Visible */}
          <Stack spacing={2}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                label="Start Time"
                value={chooseTimeStartValue}
                onChange={(v) => setChooseTimeStartValue(v)}
                format="MM/DD/YY hh:mm A"
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    sx: {
                      bgcolor: 'background.paper',
                      borderRadius: 2,
                      boxShadow: 1,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: 'text.secondary',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        backgroundColor: 'background.paper',
                        padding: '0 4px',
                        '&.Mui-focused': {
                          color: 'primary.main',
                        },
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                label="End Time"
                value={chooseTimeEndValue}
                onChange={(v) => setChooseTimeEndValue(v)}
                format="MM/DD/YY hh:mm A"
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    sx: {
                      bgcolor: 'background.paper',
                      borderRadius: 2,
                      boxShadow: 1,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: 'text.secondary',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        backgroundColor: 'background.paper',
                        padding: '0 4px',
                        '&.Mui-focused': {
                          color: 'primary.main',
                        },
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Stack>

          {/* Submit Button */}
          <Button
            fullWidth
            variant="contained"
            sx={{
              bgcolor: 'primary.main',
              color: 'background.paper',
              fontWeight: 'bold',
              borderRadius: 2,
              textTransform: 'none',
              '&:hover': { bgcolor: 'primary.light' },
              '&:disabled': { bgcolor: 'action.disabledBackground' },
            }}
            onClick={() => {
              // Validation
              if (!flow || flow === 'None') {
                setError('Please select a flow type before submitting');
                return;
              }

              if (!testCaseId) {
                setError('Please select a test case before submitting');
                return;
              }

              const startTs = chooseTimeStartValue.startOf('minute').valueOf();
              const endTs = chooseTimeEndValue.endOf('minute').valueOf();

              if (!startTs || !endTs) {
                setError('Please select valid start and end times');
                return;
              }

              setError('');
              dispatch(reset());

              // Get flowDbId and marketId from selected flow
              const selectedFlowData = filteredFlows.find((f) => f.test_case_id === testCaseId);
              const flowDbId = selectedFlowData?.flow_db_id || selectedFlowData?.flow_id;
              const marketId = selectedMarket?.id;

              // Save test metadata for export
              localStorage.setItem('lastFlow', flow);
              localStorage.setItem('testCaseId', testCaseId);
              localStorage.setItem(
                'startTimeLocal',
                chooseTimeStartValue.format('MM/DD/YYYY hh:mm A'),
              );
              localStorage.setItem('endTimeLocal', chooseTimeEndValue.format('MM/DD/YYYY hh:mm A'));

              // Get selected device database ID
              const selectedDeviceObj = devices.find((d) => d.name === device);
              const deviceDbId = selectedDeviceObj?.id || null;

              // Save device information
              if (device && device !== 'None') {
                if (selectedDeviceObj) {
                  localStorage.setItem(
                    'selectedDevice',
                    JSON.stringify({
                      name: selectedDeviceObj.name,
                      id: selectedDeviceObj.device_id_1,
                      type: selectedDeviceObj.type,
                    }),
                  );
                }
              } else {
                localStorage.removeItem('selectedDevice');
              }

              // Check if this is the mock loyalty test case - use hardcoded values
              const isMockLoyaltyTest = testCaseId === 'TC-MOCK-LOYALTY-001';

              let finalFlowDbId, finalDeviceId, finalMarketId, finalStartTs, finalEndTs;

              if (isMockLoyaltyTest) {
                // Hardcoded values for TC-MOCK-LOYALTY-001
                finalFlowDbId = 103;
                finalDeviceId = 15;
                finalMarketId = 6;
                finalStartTs = 1758899729000;
                finalEndTs = 1759339055000;
              } else {
                // Use user-selected values
                finalFlowDbId = flowDbId;
                finalDeviceId = deviceDbId;
                finalMarketId = marketId;
                finalStartTs = startTs;
                finalEndTs = endTs;
              }

              // Build metadata object for Redux storage
              const metadata = {
                flow_db_id: finalFlowDbId,
                flow_name: flow,
                test_case_id: testCaseId,
                market_id: finalMarketId,
                market_name: selectedMarket?.name || '',
                device_id: finalDeviceId, // Database ID (for API)
                device_hardware_id: selectedDeviceObj?.device_id_1 || 'Unknown', // IDFA/IDFV (for display)
                device_name: selectedDeviceObj?.name || 'Unknown',
                device_type: selectedDeviceObj?.type || 'Unknown',
                start_time: finalStartTs,
                end_time: finalEndTs,
                tester_email: userEmail,
              };

              // Dispatch validation request with all parameters
              dispatch(
                validateEventsAsync(
                  testCaseId, // testCaseId - used to look up the specific flow
                  finalDeviceId, // deviceID - database ID, not name
                  includeOutboundBeacons, // includeOutbound
                  finalStartTs, // timestampStart
                  finalEndTs, // timestampEnd
                  finalMarketId, // marketId - database ID (numeric)
                  finalFlowDbId, // flowDbId
                  metadata, // metadata object for Redux storage
                ),
              );
              navigate('/beacons');
            }}
          >
            Submit
          </Button>

          {/* Options - Toggle Switches */}
          <Stack spacing={1}>
            {/* Non-Validated Events Toggle */}
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: 1,
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={includeNonValidated}
                    onChange={handleIncludeNonValidated}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: 'secondary.main',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: 'secondary.main',
                      },
                    }}
                  />
                }
                label="Non-Validated Events"
                labelPlacement="start"
                sx={{
                  width: '100%',
                  marginLeft: 0,
                  marginRight: 0,
                  '& .MuiFormControlLabel-label': {
                    flex: 1,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                  },
                }}
              />
            </Box>

            {/* Outbound Beacons Toggle */}
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: 1,
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={includeOutboundBeacons}
                    onChange={handleIncludeOutboundBeacons}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: 'secondary.main',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: 'secondary.main',
                      },
                    }}
                  />
                }
                label="Outbound Events"
                labelPlacement="start"
                sx={{
                  width: '100%',
                  marginLeft: 0,
                  marginRight: 0,
                  '& .MuiFormControlLabel-label': {
                    flex: 1,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                  },
                }}
              />
            </Box>
          </Stack>

          {/* Error */}
          {error && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
